'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import JsonLd from '@src/components/Shared/JsonLd';
import ScrollToContainer from '@src/components/Shared/ScrollToContainer';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import ClientSuccess from '@src/components/Sections/ClientSuccess';
import NextImage from '@src/components/Shared/NextImage';
import RenderButtons from '@src/components/Shared/RenderButtons';

import ImageTextPrimary from '@src/components/Sections/ImageTextPrimary';
import CallToAction from '@src/components/Sections/CallToAction';
import { ProductsClientPropTypes } from './Products.types';
import usePreviewContent from '@src/hooks/usePreviewContent';
import constructURL from '@src/utils/constructURL';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import HotSpots from '@src/components/Sections/HotSpots';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import Testimonials from '@src/components/Sections/Testimonials';

const ProductsClient: React.FC<ProductsClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode, slug: '/products', baseURL: '' })
  );
  setContentType('site-page');

  const productsJsonLd = JsonLd({
    name: page?.pageName,
    pageUrl: `/${page?.languageCode?.toLowerCase()}${page?.slug === '/' ? '' : page?.slug}`,
    thumbnailUrl: page?.seoMetadata?.previewImage?.url,
    description: page?.seoMetadata?.description,
    datePublished: page?.sys?.firstPublishedAt,
    dateModified: page?.sys?.publishedAt,
    locale: page?.languageCode?.toLowerCase(),
  });
  return (
    <ScrollToContainer>
      {/* Add JSON-LD to your page */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productsJsonLd) }}
      />
      <div
        data-test='product-page-hero-component'
        className='mb-[6rem] flex h-[38rem] items-center bg-primary xl:mb-[7.5rem] xl:h-[68rem]'
        style={{
          backgroundImage: `url(${page?.hero?.items?.[0]?.mediaTertiary?.url})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
        data-id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
      >
        <h1 className='invisible absolute'>{page?.hero?.items?.[0]?.title}</h1>
        <Container className='mb-[0]! w-full'>
          <div className='hidden lg:block'>
            <NextImage
              className='mx-auto w-full lg:max-w-[89.9rem]'
              objectFit='cover'
              src={page?.hero?.items?.[0]?.mediaPrimary?.url}
              alt={page?.hero?.items?.[0]?.mediaPrimary?.description}
            />
          </div>
          <div className='block lg:hidden'>
            <NextImage
              className='mx-auto w-full max-w-[51rem]'
              objectFit='cover'
              src={page?.hero?.items?.[0]?.mediaSecondary?.url}
              alt={page?.hero?.items?.[0]?.mediaSecondary?.description}
            />
          </div>
        </Container>
      </div>

      <Container
        attr='tools-to-help-component'
        id={page?.moreTimeForPatients?.items?.[0]?.extraAssetsCollection?.items?.[1]?.scrollTo?.slice(
          1
        )}
      >
        <SectionHeader
          title={page?.effectiveCare?.items?.[0]?.title}
          childrenClassName='text-[1.6rem] max-w-[73.7rem]'
          titleClassName=' text-[2.8rem] leading-[3.2rem] md:leading-[4.6rem] font-light xl:text-[7.2rem] xl:leading-[7rem] max-w-[90rem] mb-[3.4rem]'
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.effectiveCare?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        </SectionHeader>
        <SectionHeader
          title={page?.moreTimeForPatients?.items?.[0]?.extraAssetsCollection?.items?.[1]?.title}
          childrenClassName='text-[1.6rem] max-w-[73.7rem]'
          titleClassName=' text-[2.8rem] leading-[3.2rem] md:leading-[4.6rem] font-light xl:text-[7.2rem] xl:leading-[7rem] max-w-[90rem] mb-[3.4rem]'
          align='center'
        >
          <RenderBodyCopy
            bodyCopy={
              page?.moreTimeForPatients?.items?.[0]?.extraAssetsCollection?.items?.[1]?.bodyCopy
                ?.json?.content
            }
          />
        </SectionHeader>
      </Container>

      <Container
        attr='less-time-on-admin-component'
        id={page?.moreTimeForPatients?.items?.[0]?.scrollTo?.slice(1)}
      >
        <SectionHeader
          className='xl:mb-[3.5rem]'
          title={page?.moreTimeForPatients?.items?.[0]?.title}
          titleClassName='text-[2.8rem] leading-[3.2rem] md:leading-[4.6rem] font-light xl:text-[4.8rem] xl:leading-[5rem] xl:max-w-[54rem] xl:locale-es:max-w-[73rem] xl:locale-fr:max-w-[90rem] mb-[3.4rem]'
          childrenClassName='text-[1.6rem] max-w-[90rem]'
          align='center'
        >
          <>
            <RenderBodyCopy
              bodyCopy={page?.moreTimeForPatients?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        </SectionHeader>
        <HotSpots
          className='mb-[8rem] p-[0]! md:mb-[14rem] 3xl:[&_figure]:left-[5%] 3xl:[&_figure]:w-[90%]'
          data={page?.moreTimeForPatients?.items?.[0]}
        />
      </Container>

      <ImageTextPrimary
        attr='itp-guesswork-component'
        id={page?.objectiveMeasurement?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.objectiveMeasurement?.items?.[0]?.preTitle}
        title={page?.objectiveMeasurement?.items?.[0]?.title}
        imgURL='https://images.ctfassets.net/98s79sqwuajy/1GRPXkixFQ6f29Z2HQxJVM/a3ece3e21d2930712ad234c9e8d4d5e7/Rectangle_7.png'
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.objectiveMeasurement?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Container>
        {/* <SectionHeader
          className="mb-[4.4rem]"
          title={page?.objectiveSolutions?.items?.[0]?.title}
          titleClassName="text-[2.8rem] leading-[3.2rem] md:leading-[4.6rem] font-light xl:text-[4.8rem] xl:leading-[5rem] max-w-[90rem]"
          align='center'
        /> */}
        <FeaturedProducts data={page?.objectiveSolutions?.items?.[0]} />
      </Container>

      <Container
        className='xl:mb-[4.7rem]!'
        id={page?.normativeData?.items?.[0]?.scrollTo?.slice(1)}
      >
        <SectionHeader
          className='mb-[4.7rem]'
          titleClassName='text-[2.8rem] xl:text-[7.2rem] leading-[3rem] md:leading-[4.6rem] xl:leading-[7.2rem] font-light mb-[3.5rem] max-w-[77.5rem]'
          pretitle={page?.normativeData?.items?.[0]?.preTitle}
          title={page?.normativeData?.items?.[0]?.title}
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.normativeData?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        </SectionHeader>
        <div data-id='hotspot-2'>
          <HotSpots
            className='mb-[8rem] p-[0]! md:mb-[4rem] 3xl:[&_figure]:left-[5%] 3xl:[&_figure]:w-[90%]'
            data={page?.normativeData?.items?.[0]}
          />
        </div>
      </Container>

      <Container attr='20mill-component' id={page?.dataPoints?.items?.[0]?.scrollTo?.slice(1)}>
        <SectionHeader
          titleClassName='text-[2.2rem] font-normal mb-[2rem] leading-[3.3rem] '
          title={page?.dataPoints?.items?.[0]?.title}
          align='center'
        >
          <>
            <RenderBodyCopy bodyCopy={page?.dataPoints?.items?.[0]?.bodyCopy?.json?.content} />
            <RenderButtons
              className='justify-left text-center md:justify-center'
              items={page?.dataPoints?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
      </Container>

      <ImageTextPrimary
        reversed
        id={page?.betterCommunication?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.betterCommunication?.items?.[0]?.preTitle}
        title={page?.betterCommunication?.items?.[0]?.title}
        imgURL={page?.betterCommunication?.items?.[0]?.mediaPrimary?.url}
        imgURLSecondaryClassName='absolute! right-[3rem] top-[4.5%] xsm:w-[20%] md:right-[9.3rem] lg:transform lg:top-[50%] lg:translate-y-[-50%] lg:right-[9.4rem] lg:w-[20%] xl:w-[25rem] xl:right-[14.8rem] 2xl:w-xs 3xl:right-[7rem]'
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={page?.betterCommunication?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <ImageTextSecondary
        className='mb-[6rem]! gap-[0rem]! md:mb-[8rem]! lg:mb-[12rem]! lg:mt-[17rem] lg:gap-[4.2rem]!'
        id={page?.toolkit?.items?.[0]?.scrollTo?.slice(1)}
        pretitle={page?.toolkit?.items?.[0]?.preTitle}
        title={page?.toolkit?.items?.[0]?.title}
        imgURL={page?.toolkit?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.toolkit?.items?.[0]?.mediaPrimary?.description ?? ''}
        imgFirstClassName='mb-[1rem]! md:mb-[0.4rem]!'
        textContainerClassName='[&>header>div]:text-unset'
        childrenElements={
          <>
            <RenderBodyCopy
              className='xl:max-w-[41rem] [&>strong]:text-primary'
              bodyCopy={page?.toolkit?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.toolkit?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextPrimary
        attr='reporting-component'
        id={page?.reporting?.items?.[0]?.scrollTo?.slice(1)}
        imgURL={page?.reporting?.items?.[0]?.mediaPrimary?.url}
        pretitle={page?.reporting?.items?.[0]?.preTitle}
        title={page?.reporting?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy bodyCopy={page?.reporting?.items?.[0]?.bodyCopy?.json?.content} />
          </>
        }
      />

      <Testimonials
        id={page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <NextImage
        src={page?.weExist?.items?.[0]?.mediaPrimary?.url}
        id={page?.weExist?.items?.[0]?.scrollTo?.slice(1)}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[5.6rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={page?.weExist?.items?.[0]?.title}
            titleClassName='text-left md:text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] lg:text-[4.8rem] 2xl:text-[6.2rem] lg:leading-[5rem] 2xl:leading-[6.2rem]'
            childrenClassName='relative max-w-[88.8rem]!'
            titleHighlightBold
            align='center'
          >
            <RenderBodyCopy
              className='mx-auto mt-[2.4rem] max-w-full! text-left text-[1.6rem] font-extralight text-body-copy--light md:text-center md:text-[2.4rem]  2xl:mt-[3.2rem] 2xl:max-w-[88.8rem]'
              bodyCopy={page?.weExist?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-left md:justify-center'
              items={page?.weExist?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </Container>
      </NextImage>

      <ClientSuccess
        id={page?.clientSuccess?.items?.[0]?.scrollTo?.slice(1)}
        data={page?.clientSuccess?.items?.[0]}
      />

      <CallToAction
        attr='call-to-action-component'
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </ScrollToContainer>
  );
};

export default ProductsClient;
